import { Component, OnInit } from '@angular/core';
import * as _ from 'lodash';
import { Subscription } from 'rxjs';
import { ByIssueMetrics, ServiceInsightsByIssueData, ServiceMetricTypes, SICallTypes } from '../../types/service-insight-types';
import { ActivatedRoute, Router } from '@angular/router';
import { FiltersService } from 'src/app/core/services/filters.service';
import { ContractModel } from 'src/app/global/contract-filter-model';
import { MixpanelService } from 'src/app/core/services/mixpanel.service';
import { ServiceInsightsService } from '../../services/service-insights-service.service';
import { MetricsService } from 'src/app/core/services/metrics.service';
import { SpreadsheetService } from 'src/app/core/services/spreadsheet.service';
import { ServiceCustomerFilterOptions, ServiceCustomerFilters } from 'src/app/global/service-customer-model';
import { PhotoSelectionStore } from 'src/app/shared/stores/photo-selection-store';

@Component({
    selector: 'app-service-details-calls',
    templateUrl: './service-details-calls.component.html',
    styleUrls: ['./service-details-calls.component.scss'],
    providers: [PhotoSelectionStore] // Provide a new instance of PhotoSelectionStore for this component and its children
    ,
    standalone: false
})
export class ServiceDetailsCallsComponent implements OnInit {

  filters: ContractModel | undefined;
  filtersSubscription: Subscription | undefined;
  pageTitle: string = '';

  downloadSpreadsheetStatus: string = '';
  spreadsheetDownloadStatus: Subscription | undefined;

  tabSelected: string = 'by-date';
  groupByProperty :string = "endDate";

  issueFilter : string = "";

  callType: SICallTypes = SICallTypes.UntrackedCalls;
  byIssuesData : ByIssueMetrics[] = [];

  //expose enum for html
  ServiceMetricTypes = ServiceMetricTypes;
  SICallTypes = SICallTypes;

  advancedFilterData: ServiceCustomerFilters[] = [];
  excludeKeys: string[] = [];
  selectedAdvancedFilters: ServiceCustomerFilterOptions = {
    "accountNumber": [],
    "accountName": [],
    "country": [],
    "state": [],
    "city": [],
    "zipCode": [],
    'recurringIssue': [],
   };
  displayLoaderForFilterData : boolean = false;
  toggleAdvancedFilterSelection: boolean = false;

  serviceCustomerFiltersSubscription: Subscription| undefined;

  drillThroughAccountNumber: string | null = null;
  drillThroughAccountName: string | null = null;
  private isInitialLoad: boolean = true;

private readonly callTypesWithRecurring: SICallTypes[] = [
  SICallTypes.ExtraServiceVisit,
  SICallTypes.RegularServiceCalls,
  SICallTypes.ProgramReviews,
];
  constructor(
    private route: ActivatedRoute,
    private _filterService: FiltersService,
    private _serviceInsightsService: ServiceInsightsService,
    private _metricsService: MetricsService,
    private _router: Router,
    private _mixpanelService: MixpanelService,
    private _spreadsheetService: SpreadsheetService,
    private _photoSelectionStore: PhotoSelectionStore,
  ) {
    const navigation = this._router.currentNavigation();
    if (navigation?.extras.state) {
      this.drillThroughAccountNumber = navigation.extras.state['accountNumber'] || null;
      this.drillThroughAccountName = navigation.extras.state['accountName'] || null;
    }
  }

  async ngOnInit() {

    this.selectedAdvancedFilters = {
      "accountNumber": [],
      "accountName": [],
      "country": [],
      "state": [],
      "city": [],
      "zipCode": [],
      'recurringIssue': []
     };

     //Send an alert if there is no data to force reinput of filters on reload
    //Check if all filter params are at initial values
    this.filtersSubscription = this._filterService.selectedFiltersObservable.subscribe(f => this.filters = f);
    if ( this.filters == undefined )
    {
      alert('Please apply filters before navigating to Service Insights')
      this._router.navigate(['/filter']);
    }

    this.pageTitle = this.route.snapshot.params['callType'];
    this.pageTitle = this.pageTitle.split("-").map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(" ");

    this.callType = this._metricsService.mapCallTypesToKey(this.pageTitle);
    
    // Set excludeKeys based on callType to conditionally exclude 'recurringIssue' filter for certain call types
    // Recurring issue filter is only relevant for Regular Service Calls, Extra Service Requests and Program Reviews, so we exclude it for other call types to avoid confusion
    if (!this.callTypesWithRecurring.includes(this.callType)) {
      this.excludeKeys = ['recurringIssue'];        
    }
    
    this._mixpanelService.track(`Service Insights ${this.pageTitle} ${this.tabSelected} initial tab loaded`,{
      'Page Name': this.pageTitle,
      'Tab Selected': this.tabSelected
    });

    if(this.callType !== SICallTypes.PhotoObservations){
      // TODO: Remove once legacy UI is retired. service-details-table now owns this internally.
      this.subscribeToByIssuesData();
      this.getByIssuesData();
    }

    this.spreadsheetDownloadStatus = this._spreadsheetService.downloadStatus$.subscribe(status => {
      this.downloadSpreadsheetStatus = status;
    });

    this.serviceCustomerFiltersSubscription = this._serviceInsightsService.getServiceCustomerFilters().subscribe(async advFilter => {
      this.selectedAdvancedFilters = advFilter;

      // Skip initial subscription emission to prevent clearing drill-through state before auto-apply
      if (!this.isInitialLoad && (!advFilter.accountNumber || advFilter.accountNumber.length === 0)) {
        this.drillThroughAccountNumber = null;
        this.drillThroughAccountName = null;
      }
      this.isInitialLoad = false;
      //refetch data if not photo observations drill-in
      if(this.callType !== SICallTypes.PhotoObservations){
        // TODO: Remove once legacy UI is retired. service-details-table now refreshes this internally.
        this.getByIssuesData(true);
      }
    });

    // TODO: Remove once legacy UI is retired. service-details-table now fetches advanced filter data internally.
    // Get Advanced Filter Options for Summary Page
    this.displayLoaderForFilterData=true;
    this.advancedFilterData = await this.getAdvancedFilterData(this.callType);
    this.displayLoaderForFilterData=false;

    //drillThroughAccountNumber is only for SICallType.ExtraServiceVisits
    if (this.drillThroughAccountNumber) {
      this.selectedAdvancedFilters.accountNumber = [this.drillThroughAccountNumber];
      this._serviceInsightsService.setServiceCustomerFilters(this.selectedAdvancedFilters);
    }

    //clear session storage on call type change
    this.clearCachedData();
  }

  ngOnDestroy(): void {
    this.spreadsheetDownloadStatus?.unsubscribe();
    this.serviceCustomerFiltersSubscription?.unsubscribe();
  }

  /**
   * @deprecated US612165 - Remove once legacy UI is retired. service-details-table now subscribes to by-issues data internally.
   */
  subscribeToByIssuesData() {
    this._serviceInsightsService.getServiceInsightsByIssuesData(this.callType as keyof ServiceInsightsByIssueData).subscribe(
      (d) => {
        this.byIssuesData = d;
      }
    );
  }

  /**
   * @deprecated US612165 - Remove once legacy UI is retired. service-details-table now fetches by-issues data internally.
   */
  async getByIssuesData(refresh = false) {
    if ((this.byIssuesData != undefined && this.byIssuesData.length == 0) || refresh) {
      this._serviceInsightsService.setServiceInsightsByIssuesData(this.callType as keyof ServiceInsightsByIssueData,
        await this._serviceInsightsService.getSIMetricByIssuesFromAPI(ServiceMetricTypes.Calls, this.callType, this.selectedAdvancedFilters)
      )
    }
  }

  clearCachedData(): void {
    ['byPeriodData', 'byLocationData', 'byPeriodPrevData', 'byIssueData', 'expandedPeriodData','expandedLocationsData','expandedIssuesData', 'currentDataFilter'].forEach((key) => {
      sessionStorage.removeItem(key);
    });
  }

  /**
   * @deprecated US612165 - Remove once legacy UI is retired. Used only by the legacy bubble chart (bubbleClick).
   * Refreshed UI handles issue filtering inside service-details-table via service-details-issues-filter.
   */
  handleTooltipClick(issueFilterName : string): void {
    this.issueFilter = issueFilterName
  }


  changeTab(tab: string) {
    const startTime = performance.now();
    this.tabSelected = tab;
    this.setGroupByProperty(this.tabSelected)
    this.issueFilter = "";
    this._photoSelectionStore.clear();
    const endTime = performance.now();
    const duration = endTime - startTime;
    this._mixpanelService.track('Service Insights ' + this.pageTitle + this.tabSelected  + ' tab Changed',{
      'Page Name': this.pageTitle,
      'Tab Selected': tab,
      'Duration': duration
    });

  }

  setGroupByProperty(tabSelected: string): void {
    if(tabSelected === "by-date") {
      this.groupByProperty = "endDate";
    } else if(tabSelected === "by-location") {
      this.groupByProperty = "accountNumber";
    } else if(tabSelected === "photo") {
      this.groupByProperty = "endDate";
    } else {
      this.groupByProperty = "issues";
    }
  }

  /**
   * @deprecated US612165 - Remove once legacy UI is retired. Used only by the legacy filter-indication-container.
   */
  getIndicationText(): string {
    switch(this.groupByProperty) {
      case 'issues':
        return 'Issues Selected : ';
      case 'accountNumber':
        return 'Location Selected : ';
      case 'endDate':
        return 'Date Selected : ';
      default:
        return '';
    }
  }

  /**
   * @deprecated US612165 - Remove once legacy UI is retired. Used only by the legacy filter-indication-container.
   */
  // get the current value for the filter
  getLegendValue(graphFilterApplied: string | null | undefined){
    const monthNames = ["January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"];
    if(!graphFilterApplied) return "No Filters Applied"
    if(this.groupByProperty === "endDate"){
      const date = new Date(graphFilterApplied);
      const utcYear = date.getUTCFullYear();
      const utcMonth = date.getUTCMonth();
      const utcDay = date.getUTCDate();
      const formattedDate = `${monthNames[utcMonth]} ${utcDay}, ${utcYear}`;
      return formattedDate;
    }
    return graphFilterApplied;
  }
  
  /**
   * @deprecated US612165 - Remove once legacy UI is retired. service-details-table now fetches advanced filter data internally.
   */
  async getAdvancedFilterData(callType?: SICallTypes) : Promise<ServiceCustomerFilters[]> {
    return await this._serviceInsightsService.getServiceInsightsAdvFilters(callType);
  }
  
  /**
   * @deprecated US612165 - Remove once legacy UI is retired. Only called from legacy advanced-filters (photos section and calls chart section).
   * Refreshed UI advanced-filters are handled inside photos-table and service-details-table respectively.
   */
  async onSelectedFiltersChange(selectedFilters:{ [id: string]: string[] }) {
    this._serviceInsightsService.clearAllExpandedPhotos();
    const serviceCustomerFilters = selectedFilters as ServiceCustomerFilterOptions;
    this._serviceInsightsService.setServiceCustomerFilters(serviceCustomerFilters);
  }

  clearAccountFilter() {
    this.selectedAdvancedFilters = {
      ...this.selectedAdvancedFilters,
      accountNumber: []
    };
    this._serviceInsightsService.setServiceCustomerFilters(this.selectedAdvancedFilters);
  }
}
